// Country name to ISO 3166-1 alpha-2 code mapping
// This mapping covers the most common countries that might appear in deal data
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  // Major economies and common countries
  "United States": "us",
  "United States of America": "us",
  USA: "us",
  US: "us",
  "United Kingdom": "gb",
  UK: "gb",
  "Great Britain": "gb",
  England: "gb",
  Scotland: "gb",
  Wales: "gb",
  "Northern Ireland": "gb",
  Canada: "ca",
  Australia: "au",
  Germany: "de",
  France: "fr",
  Japan: "jp",
  China: "cn",
  India: "in",
  Brazil: "br",
  Russia: "ru",
  "South Korea": "kr",
  Italy: "it",
  Spain: "es",
  Netherlands: "nl",
  Switzerland: "ch",
  Sweden: "se",
  Norway: "no",
  Denmark: "dk",
  Finland: "fi",
  Belgium: "be",
  Austria: "at",
  Ireland: "ie",
  Israel: "il",
  Singapore: "sg",
  "Hong Kong": "hk",
  "New Zealand": "nz",
  "South Africa": "za",
  Mexico: "mx",
  Argentina: "ar",
  Chile: "cl",
  Colombia: "co",
  Peru: "pe",
  Venezuela: "ve",
  Poland: "pl",
  "Czech Republic": "cz",
  Hungary: "hu",
  Romania: "ro",
  Bulgaria: "bg",
  Croatia: "hr",
  Slovenia: "si",
  Slovakia: "sk",
  Estonia: "ee",
  Latvia: "lv",
  Lithuania: "lt",
  Greece: "gr",
  Portugal: "pt",
  Turkey: "tr",
  Egypt: "eg",
  "Saudi Arabia": "sa",
  "United Arab Emirates": "ae",
  UAE: "ae",
  Qatar: "qa",
  Kuwait: "kw",
  Bahrain: "bh",
  Oman: "om",
  Jordan: "jo",
  Lebanon: "lb",
  Morocco: "ma",
  Tunisia: "tn",
  Algeria: "dz",
  Nigeria: "ng",
  Kenya: "ke",
  Ghana: "gh",
  Ethiopia: "et",
  Tanzania: "tz",
  Uganda: "ug",
  Rwanda: "rw",
  Botswana: "bw",
  Namibia: "na",
  Zambia: "zm",
  Zimbabwe: "zw",
  Mozambique: "mz",
  Madagascar: "mg",
  Mauritius: "mu",
  Seychelles: "sc",
  Thailand: "th",
  Vietnam: "vn",
  Malaysia: "my",
  Indonesia: "id",
  Philippines: "ph",
  Taiwan: "tw",
  "North Korea": "kp",
  Mongolia: "mn",
  Kazakhstan: "kz",
  Uzbekistan: "uz",
  Turkmenistan: "tm",
  Kyrgyzstan: "kg",
  Tajikistan: "tj",
  Afghanistan: "af",
  Pakistan: "pk",
  Bangladesh: "bd",
  "Sri Lanka": "lk",
  Nepal: "np",
  Bhutan: "bt",
  Maldives: "mv",
  Myanmar: "mm",
  Cambodia: "kh",
  Laos: "la",
  Brunei: "bn",
  "Papua New Guinea": "pg",
  Fiji: "fj",
  Samoa: "ws",
  Tonga: "to",
  Vanuatu: "vu",
  "Solomon Islands": "sb",
  Palau: "pw",
  "Marshall Islands": "mh",
  Micronesia: "fm",
  Kiribati: "ki",
  Nauru: "nr",
  Tuvalu: "tv",
};

/**
 * Converts a country name to its ISO 3166-1 alpha-2 country code
 * @param countryName - The full country name
 * @returns The 2-letter country code in lowercase, or null if not found
 */
export function getCountryCode(countryName: string | null): string | null {
  if (!countryName) return null;

  // Try exact match first
  const exactMatch = COUNTRY_NAME_TO_CODE[countryName];
  if (exactMatch) return exactMatch;

  // Try case-insensitive match
  const normalizedName = countryName.trim();
  const caseInsensitiveMatch = Object.keys(COUNTRY_NAME_TO_CODE).find((key) => key.toLowerCase() === normalizedName.toLowerCase());

  if (caseInsensitiveMatch) {
    return COUNTRY_NAME_TO_CODE[caseInsensitiveMatch];
  }

  // If it's already a 2-letter code, return it lowercase
  if (normalizedName.length === 2 && /^[a-zA-Z]{2}$/.test(normalizedName)) {
    return normalizedName.toLowerCase();
  }

  // Return null if no match found
  return null;
}

/**
 * Checks if a country code is valid for flagcdn.com
 * @param countryCode - The 2-letter country code
 * @returns boolean indicating if the code is valid
 */
export function isValidCountryCode(countryCode: string | null): boolean {
  if (!countryCode) return false;
  return /^[a-z]{2}$/.test(countryCode);
}
